import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {validateIssueBody} from '@github-ui/entity-validators'
import {IssueBodyEditor} from '@github-ui/issue-body/components/IssueBodyEditor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {clsx} from 'clsx'
import {useMemo, useCallback, useRef, type RefObject} from 'react'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SyntheticChangeEmitter} from '@github-ui/use-synthetic-change'
import {Stack} from '@primer/react/experimental'

import {useUpdateDescriptionMutation} from '../../mutations/use-update-description-mutation'
import styles from './DescriptionEditor.module.css'
import {
  useLayoutBaseRepository,
  useLayoutBaseSha,
  useLayoutHeadSha,
  useLayoutPullRequestId,
  useLayoutPullRequestTitle,
} from '../../stores/selectors'
import {useCommentBoxSubject} from '@github-ui/diff-lines/use-comment-box-subject'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {StaleContentError} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {md2html} from '@github-ui/md2html'
import {announce} from '@github-ui/aria-live'
import {isStringOrUndefined} from '../../utils/validators'
import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {useCrossRepositoryLinkClick} from '../../hooks/use-cross-repository-link-navigation'
import {useUserCopilotAccess} from '../../hooks/use-user-copilot-access'
import {MutationFailureBanner} from '../FailureBanner'
import {PullRequestSummaryBanner} from './copilot-summary/PullRequestSummaryBanner'
import {PullRequestSummaryToolbarButton} from './copilot-summary/PullRequestSummaryToolbarButton'
import {usePullRequestSummary} from './copilot-summary/use-pull-request-summary'

const PLACEHOLDER_TEXT = 'No description provided.'
const DESCRIPTION_SAVE_ERROR = 'Failed to update description'

/** Session storage key for persisting an unsaved PR description draft. */
const prDescriptionDraftKey = (pullRequestId: number) => `pr-description-draft-${pullRequestId}`

type DescriptionEditorProps = {
  /** Current description as sanitized HTML, or null/undefined if none. Used by the read-only viewer. */
  bodyHtml: SafeHTMLString | null | undefined
  /** Current description as raw markdown. Used as the initial draft when editing. */
  body: string
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
  /** Class for the viewer container; ignored when editing. */
  viewerClassName?: string
  /** Element to receive focus when the editor is dismissed (e.g. the edit trigger). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Shared description display + editing experience for pull requests. Handles
 * toggling between a `NewMarkdownViewer` and a `CommentBox` editor, draft
 * persistence, stale-content detection, and saving via `useUpdateDescriptionMutation`.
 *
 * The parent owns the `isEditing` state so it can place the edit trigger
 * wherever it makes sense (e.g. an overview kebab menu, a panel title bar).
 * While editing, the trigger should remain mounted but marked `inactive`.
 */
export function DescriptionEditor({
  bodyHtml,
  body,
  isEditing,
  onEditingChange,
  viewerClassName,
  returnFocusRef,
}: DescriptionEditorProps) {
  const pullRequestId = useLayoutPullRequestId()
  const pullRequestTitle = useLayoutPullRequestTitle()
  const baseRepository = useLayoutBaseRepository()
  const headSha = useLayoutHeadSha()
  const baseSha = useLayoutBaseSha()
  const handleLinkClick = useCrossRepositoryLinkClick()

  const mutation = useUpdateDescriptionMutation()

  const optimisticBody = mutation.isPending ? mutation.variables.body : body
  const optimisticBodyHtml = mutation.isPending ? md2html(optimisticBody) : (bodyHtml ?? '')
  const isEmptyDescription = optimisticBodyHtml === ''

  const [draft, setDraft, clearDraft] = useLocalStorage(prDescriptionDraftKey(pullRequestId), undefined, {
    validator: isStringOrUndefined,
  })

  const editorValue = draft ?? body

  const repoVisibility = baseRepository.public ? 'PUBLIC' : 'PRIVATE'
  const shouldShowCopilot = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: repoVisibility,
    userCanPushToRepo: baseRepository.currentUserCanPush ?? false,
  })

  const prSummaryPromptEditorEnabled = isFeatureEnabled('prs_overview_copilot_summary_prompt_editor')
  const hasCopilotAccess = useUserCopilotAccess()

  const completionsUrl = `/${baseRepository.ownerLogin}/${baseRepository.name}/copilot/completions/diff_summary`

  const inputBoxRef = useRef<CommentBoxHandle>(null)

  // Classic (non-hybrid) markdown editor exposes its textarea here, letting the placeholder/
  // completion be inserted at the user's last selection instead of always at the end of the body.
  const getEditorTextarea = useCallback(() => {
    const input = inputBoxRef.current?.inputRef.current
    return input instanceof HTMLTextAreaElement || input instanceof HTMLInputElement ? input : null
  }, [])

  // `emitChange` drives the editor the same way the user typing would (preserving caret, any
  // unrelated selection, and undo history), so there's no need to separately track/restore a
  // pending selection the way a plain `setState`-driven value update would require.
  const emitEditorChange = useCallback<SyntheticChangeEmitter>((insertValue, replaceRange, newSelection) => {
    inputBoxRef.current?.emitChange(insertValue, replaceRange, newSelection)
  }, [])

  const prSummary = usePullRequestSummary({
    baseRevision: baseSha,
    headRevision: headSha,
    headRepositoryId: baseRepository.id,
    completionsUrl,
    getEditorElement: getEditorTextarea,
    emitChange: emitEditorChange,
  })

  const closeEditor = () => {
    prSummary.discardGeneration()
    onEditingChange(false)
    if (returnFocusRef) {
      requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }

  const subject = useCommentBoxSubject({surface: 'description'})

  const validationResult = useMemo(() => validateIssueBody(editorValue), [editorValue])

  const handleSave = () => {
    if (mutation.isPending || !validationResult.isValid) return

    mutation.mutate(
      {body: editorValue},
      {
        onSuccess: () => {
          announce('Pull request description updated successfully.')
          clearDraft()
        },
        // revert back to edit mode on error
        onError: () => onEditingChange(true),
      },
    )

    // Optimistically revert to read mode
    closeEditor()
  }

  // This is intentionally a separate mutation instance from the main edit functionality, so that it doesn't share in
  // the optimistic update logic (which looks janky when interacting with checkboxes because the client side Markdown
  // renderer md2html doesn't support checkboxes).
  const directEditMutation = useUpdateDescriptionMutation()

  /** Called when Markdown is updated without entering edit mode (ie, checkbox interaction). */
  const handleMarkdownChange = async (newValue: string) => {
    try {
      await directEditMutation.mutateAsync({body: newValue})
    } catch {
      /* noop: TSQ will handle the error in addition to rejecting the promise */
    }
  }

  const bodyIsStale = mutation.error instanceof StaleContentError

  const onCancel = () => {
    if (!bodyIsStale) clearDraft()
    mutation.reset()
    closeEditor()
  }

  const isPending = mutation.isPending || directEditMutation.isPending

  const directErrorBannerReturnFocusRef = useRef<HTMLDivElement>(null)

  if (isEditing) {
    return (
      <Stack direction="vertical" gap="condensed">
        {!bodyIsStale && (
          <MutationFailureBanner title={DESCRIPTION_SAVE_ERROR} mutation={mutation} returnFocusRef={inputBoxRef} />
        )}
        <PullRequestSummaryBanner summary={prSummary} />
        <IssueBodyEditor
          editorDisabled={mutation.isPending}
          saveDisabled={prSummary.isGenerating}
          subjectId={pullRequestId.toString()}
          subject={subject}
          body={editorValue}
          bodyIsStale={bodyIsStale}
          onChange={setDraft}
          onCancel={onCancel}
          onCommit={handleSave}
          repoOwner={baseRepository.ownerLogin}
          repoName={baseRepository.name}
          title={pullRequestTitle}
          shouldShowCopilotFunctionality={shouldShowCopilot}
          trailingIcon={false}
          ref={inputBoxRef}
          additionalToolbarButtons={
            hasCopilotAccess && (
              <PullRequestSummaryToolbarButton promptEditorEnabled={prSummaryPromptEditorEnabled} summary={prSummary} />
            )
          }
        />
      </Stack>
    )
  }

  return (
    <div className={clsx(viewerClassName, isPending && styles.pendingBody)}>
      {isEmptyDescription ? (
        <div className="fgColor-muted text-italic">{PLACEHOLDER_TEXT}</div>
      ) : (
        // Normal block flow lets the description wrap around floated edit controls.
        <div tabIndex={-1} ref={directErrorBannerReturnFocusRef}>
          <NewMarkdownViewer
            verifiedHTML={optimisticBodyHtml}
            disabled={isPending}
            markdownValue={optimisticBody}
            onChange={handleMarkdownChange}
            onLinkClick={handleLinkClick}
          />
          {/* The error banner can appear in read mode when markdown updates (checkboxes) fail to save */}
          <MutationFailureBanner
            className={styles.errorBanner}
            mutation={directEditMutation}
            title={DESCRIPTION_SAVE_ERROR}
            returnFocusRef={directErrorBannerReturnFocusRef}
          />
        </div>
      )}
    </div>
  )
}
